import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { TBS_API_URL, TBS_TENANT_ID } from "../../config";
import type {
  Contact,
  ContactInput,
  ContactList,
  ContactListParams,
} from "../../types/tbs/contacts";

// Tiendas Biai — contacts.
//
// A **separate slice with its own base URL**, not an endpoint added to one of the storefront's. The
// two systems have different APIs, different auth tenants and different lifecycles; sharing a slice
// would mean a TBS change could invalidate a storefront cache tag.

export const tbsContactsApi = createApi({
  reducerPath: "tbsContactsApi",
  tagTypes: ["TbsContact"],
  baseQuery: fetchBaseQuery({
    baseUrl: TBS_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");

      // **Only when configured.** With one membership the authorizer picks the company itself, and
      // sending an empty or wrong header would be worse than sending none: a tenant the caller does
      // not belong to is a refusal, not a fallback.
      if (TBS_TENANT_ID) headers.set("X-Tenant-Id", TBS_TENANT_ID);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getContacts: builder.query<ContactList, ContactListParams | void>({
      query: (params) => {
        const search = new URLSearchParams();
        const p = params ?? {};
        if (p.q) search.set("q", p.q);
        if (p.kind) search.set("kind", p.kind);
        if (p.include_deleted) search.set("include_deleted", "true");
        if (p.start) search.set("start", String(p.start));
        if (p.limit) search.set("limit", String(p.limit));
        if (p.metadata) search.set("metadata", "true");
        const qs = search.toString();
        return `/v1/contacts${qs ? `?${qs}` : ""}`;
      },
      // Per-row tags, so editing one contact does not refetch the list of every other. The `LIST`
      // tag is what create/delete/restore invalidate, since those change which rows exist.
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((c) => ({ type: "TbsContact" as const, id: c.id })),
              { type: "TbsContact" as const, id: "LIST" },
            ]
          : [{ type: "TbsContact" as const, id: "LIST" }],
    }),

    getContact: builder.query<Contact, string>({
      query: (id) => `/v1/contacts/${id}`,
      providesTags: (_result, _error, id) => [{ type: "TbsContact", id }],
    }),

    createContact: builder.mutation<Contact, ContactInput>({
      query: (body) => ({ url: "/v1/contacts", method: "POST", body }),
      invalidatesTags: [{ type: "TbsContact", id: "LIST" }],
    }),

    /**
     * `If-Match` carries the version, and it is **required** — the server refuses a PUT without one.
     *
     * The version therefore has to travel from the row the form was loaded with, which is why it is
     * an argument here rather than something the slice could look up: looking it up would fetch the
     * current version and defeat the entire mechanism, turning "somebody else edited this" into a
     * silent overwrite.
     */
    updateContact: builder.mutation<Contact, { id: string; version: number; body: ContactInput }>({
      query: ({ id, version, body }) => ({
        url: `/v1/contacts/${id}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "TbsContact", id },
        { type: "TbsContact", id: "LIST" },
      ],
    }),

    /** Soft delete — the row moves to the `Papelera`, it is never removed. */
    deleteContact: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/contacts/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "TbsContact", id },
        { type: "TbsContact", id: "LIST" },
      ],
    }),

    restoreContact: builder.mutation<Contact, string>({
      query: (id) => ({ url: `/v1/contacts/${id}/restore`, method: "POST" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "TbsContact", id },
        { type: "TbsContact", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useRestoreContactMutation,
} = tbsContactsApi;
